import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Leaf, Heart, Globe, Award, Play } from 'lucide-react';
import { videos } from '@/data/datos';
import type { Producto, Embajador } from '@/types';
import PuntosVentaSection from '../components/ui-custom/PuntosVentaSection';

type Page = 'inicio' | 'embajador' | 'catalogo' | 'tratamientos';

interface PuntoVenta {
  id: number;
  direccion: string;
  ciudad: string;
  google_maps_url: string;
}

interface InicioProps {
  onNavigate: (page: Page, params?: { linea?: string; producto?: Producto }) => void;
  ambassador: Embajador | null;
}

export default function Inicio({ onNavigate, ambassador }: InicioProps) {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  
  const heroRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  // Helper to get YouTube ID from URL
  const getYoutubeId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url?.match(regExp);
    return (match && match[2].length === 11) ? match[2] : 'M4cLxzA6MVI';
  };

  const videoId = getYoutubeId(ambassador?.inicio_video_url || videos.inicio);
  const displayName = ambassador?.showLeaderVideoInfoInicio ? ambassador.leaderName : ambassador?.nombre_completo;
  const displayRank = ambassador?.showLeaderVideoInfoInicio ? ambassador.leaderRank : ambassador?.rango;


  // Animaciones al hacer scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    [heroRef, featuresRef, ctaRef].forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  const isVisible = (id: string) => visibleSections.has(id);

  // Datos de puntos de venta desde el embajador
  const puntosVentaAMostrar: PuntoVenta[] = ambassador?.puntos_venta || [];

  return (
    <div className="min-h-screen bg-[#f8f9f5]">
      {/* Hero Section */}
      <section 
        id="hero"
        ref={heroRef}
        className="relative min-h-screen flex items-center pt-24 overflow-hidden"
      >
        {/* Background Gradient con toque rosa */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#f8f9f5] via-white to-[#ffe4f0]/30 opacity-80" />
        
        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#8abd31]/5 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#e91e8c]/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/3 right-1/4 w-48 h-48 bg-[#8abd31]/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }} />
        </div>

        <div className="relative z-10 w-full section-padding">
          <div className="max-w-7xl mx-auto">
            {/* En móvil: Video primero, luego contenido */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              
              <div className="relative order-1 lg:order-2">
                <div 
                  className={`relative rounded-3xl overflow-hidden shadow-2xl transition-all duration-1000 ${
                    isVisible('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ boxShadow: '0 25px 50px -12px rgba(233, 30, 140, 0.25)' }}
                >
                  <div className="aspect-video relative">
                    {!isVideoPlaying ? (
                      <div 
                        className="absolute inset-0 flex items-center justify-center cursor-pointer group"
                        onClick={() => setIsVideoPlaying(true)}
                      >
                        <img 
                          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} 
                          alt={`Video Thumbnail - ${displayName}`}
                          className="absolute inset-0 w-full h-full object-cover"
                          fetchPriority="high"
                          width={480}
                          height={360}
                        />
                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                        <div className="relative z-10 w-[75px] h-[75px] md:w-20 md:h-20 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
                          <Play className="w-[27px] h-[27px] md:w-8 md:h-8 text-[#e91e8c] ml-1" />
                        </div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="text-white font-medium">{displayName}</div>
                          <div className="text-white/70 text-sm">{displayRank}</div>
                        </div>
                      </div>
                    ) : (
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                        title="Video de bienvenida Ismerely"
                        className="absolute inset-0 w-full h-full"
                        style={{ 
                          border: 'none',
                          transform: 'translateZ(0)'
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="eager"
                      />
                    )}
                  </div>
                </div>
                
                {/* Decorative Elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#e91e8c]/20 rounded-full blur-xl pointer-events-none" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#8abd31]/10 rounded-full blur-xl pointer-events-none" />
              </div>

              {/* Content - En móvil va segundo */}
              <div className="space-y-8 order-2 lg:order-1">
                <div 
                  className={`inline-flex items-center gap-2 px-4 py-2 bg-[#e91e8c]/10 rounded-full transition-all duration-700 delay-100 ${
                    isVisible('hero') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                  }`}
                >
                  {/* <Sparkles className="w-4 h-4 text-[#e91e8c]" /> */}
                  <span className="text-sm font-medium text-[#e91e8c]">100% Orgánico • COFEPRIS • FDA</span>
                </div>

                <h1 
                  className={`font-serif text-4xl md:text-5xl lg:text-6xl leading-tight transition-all duration-700 delay-200 ${
                    isVisible('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                >
                  <span className="text-[#8abd31]">Transforma</span> <span className="text-[#8abd31]">tu</span>{' '}
                  <span className="bg-gradient-to-r from-[#8abd31] to-[#e91e8c] bg-clip-text text-transparent">salud</span>{' '}
                  <span className="text-[#8abd31]">naturalmente</span>
                </h1>

                <p 
                  className={`text-lg md:text-xl text-gray-600 max-w-xl leading-relaxed transition-all duration-700 delay-300 ${
                    isVisible('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                >
                  Descubre el poder de la naturaleza con nuestros suplementos 100% orgánicos. 
                  Ismerely combina trofología con nutrición holística para tu bienestar integral.
                </p>

                <div 
                  className={`flex flex-wrap gap-4 transition-all duration-700 delay-400 ${
                    isVisible('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                >
                  <button 
                    onClick={() => onNavigate('embajador')}
                    className="btn-accent flex items-center gap-2 group shadow-lg shadow-[#e91e8c]/30"
                  >
                    <span>Conoce más</span>
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                  </button>
                  <button 
                    onClick={() => onNavigate('catalogo')}
                    className="px-6 py-3 border-2 border-[#8abd31] text-[#8abd31] rounded-full font-medium transition-all duration-300 hover:bg-[#8abd31] hover:text-white hover:shadow-lg"
                  >
                    Ver productos
                  </button>
                </div>

                {/* Stats */}
                <div 
                  className={`flex flex-wrap gap-8 pt-8 border-t border-gray-200 transition-all duration-700 delay-500 ${
                    isVisible('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                >
                  <div className="group">
                    <div className="text-3xl font-serif font-bold text-[#e91e8c] group-hover:scale-110 transition-transform">+90</div>
                    <div className="text-sm text-gray-500">Productos</div>
                  </div>
                  <div className="group">
                    <div className="text-3xl font-serif font-bold text-[#8abd31] group-hover:scale-110 transition-transform">4</div>
                    <div className="text-sm text-gray-500">Líneas</div>
                  </div>
                  <div className="group">
                    <div className="text-3xl font-serif font-bold text-[#e91e8c] group-hover:scale-110 transition-transform">+90</div>
                    <div className="text-sm text-gray-500">Tratamientos</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section 
        id="features"
        ref={featuresRef}
        className="py-24 bg-white relative overflow-hidden"
      >
        {/* Fondo decorativo rosa */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#e91e8c]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#8abd31]/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="section-padding relative z-10">
          <div className="max-w-7xl mx-auto">
            <div
              className={`text-center mb-16 transition-all duration-700 ${
                isVisible('features') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              {/* <div className="inline-flex items-center px-4 py-2 bg-[#e91e8c]/10 rounded-full mb-4">
                <span className="text-sm font-medium text-[#e91e8c]">Nuestra Esencia</span>
              </div> */}
              <h2 className="font-serif text-4xl md:text-5xl text-[#8abd31] mb-4"> ¿Por qué elegir <span className="text-[#e91e8c]">Ismerely</span>?
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Productos elaborados con los más altos estándares de calidad, respaldados por certificaciones internacionales.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: Leaf,
                  title: '100% Orgánico',
                  description: 'Productos naturales con ingredientes de la más alta calidad',
                  color: '#8abd31'
                },
                {
                  icon: Award,
                  title: 'Certificado',
                  description: 'Registro COFEPRIS y permisos FDA en Estados Unidos',
                  color: '#e91e8c'
                },
                {
                  icon: Globe,
                  title: 'Expansión Global',
                  description: 'Presentes en México, USA y Parte de Centroamérica',
                  color: '#8abd31'
                },
                {
                  icon: Heart,
                  title: 'Bienestar Integral',
                  description: 'Trofología + Nutrición Holística',
                  color: '#e91e8c'
                }
              ].map((feature, index) => (
                <div
                  key={feature.title}
                  className={`group p-8 rounded-2xl bg-[#f8f9f5] hover:bg-white hover:shadow-xl transition-all duration-500 hover:-translate-y-2 ${
                    isVisible('features') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ 
                    transitionDelay: isVisible('features') ? `${index * 100}ms` : '0ms',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                  }}
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${feature.color}15` }}
                  >
                    <feature.icon className="w-7 h-7 transition-colors duration-300" style={{ color: feature.color }} />
                  </div>
                  <h3 className="font-serif text-xl text-[#8abd31] mb-3 group-hover:text-[#e91e8c] transition-colors">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Puntos de Venta Section */}
      <PuntosVentaSection puntosVenta={puntosVentaAMostrar} ambassador={ambassador} />

      {/* CTA Section */}
      <section 
        id="cta"
        ref={ctaRef}
        className="py-24 relative overflow-hidden"
      >
        {/* Gradiente con rosa */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#8abd31] via-[#8abd31] to-[#e91e8c]" />
        
        {/* Elementos decorativos */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#e91e8c]/20 rounded-full blur-3xl" />
        
        <div className="section-padding relative z-10">
          <div 
            className={`max-w-4xl mx-auto text-center transition-all duration-700 ${
              isVisible('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
              {/* <Sparkles className="w-4 h-4 text-white" /> */}
              <span className="text-sm font-medium text-white">Únete a la familia Ismerely</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-white mb-6">
              Sé parte del <span className="text-[#ffe4f0]">cambio</span>
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Únete a nuestra red de embajadores y comienza a transformar vidas, 
              incluida la tuya. Baja inversión, alta rentabilidad, cero riesgos.
            </p>
            <button
              onClick={() => onNavigate('embajador')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#e91e8c] rounded-full font-medium transition-all duration-300 hover:shadow-2xl hover:scale-105 hover:bg-[#ffe4f0]"
            >
              <span>¡Quiero ser embajador!</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
